import { LoginModal } from '@iheartjane/menu';
import type { Nullable } from '@iheartjane/types';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { GoogleSignInButton } from '@jane/bloom/components/googleSignIn/googleSignIn';
import { useBloomAuthHandlers } from '@jane/bloom/hooks/account/useBloomAuthHandlers';
import type { BloomAuthSuccess } from '@jane/bloom/hooks/account/useBloomAuthHandlers';

interface OpenAuthModalOptions {
  onSuccess?: (result: BloomAuthSuccess) => void;
  skipBagAfterAuth?: boolean;
}

interface AuthModalContextValue {
  closeAuthModal: () => void;
  isAuthModalOpen: boolean;
  openAuthModal: (options?: OpenAuthModalOptions) => void;
}

const noopContext: AuthModalContextValue = {
  closeAuthModal: () => undefined,
  isAuthModalOpen: false,
  openAuthModal: () => undefined,
};

const AuthModalContext = createContext<AuthModalContextValue>(noopContext);

export const useAuthModal = () => useContext(AuthModalContext);

interface AuthModalProviderProps {
  children: ReactNode;
  googleSsoClientId?: Nullable<string>;
}

/**
 * Provides `useAuthModal()` which returns
 * {
 *   closeAuthModal: () => void;
 *   isAuthModalOpen: boolean;
 *   openAuthModal: (options?: OpenAuthModalOptions) => void;
 * }
 */
export const AuthModalProvider = ({
  children,
  googleSsoClientId,
}: AuthModalProviderProps) => {
  const [openOptions, setOpenOptions] = useState<OpenAuthModalOptions | null>(
    null
  );
  const isOpen = openOptions !== null;

  const closeAuthModal = useCallback(() => setOpenOptions(null), []);

  const openAuthModal = useCallback((options?: OpenAuthModalOptions) => {
    setOpenOptions(options ?? {});
  }, []);

  // Modal logins always stay on the page (no hard redirect): close the modal,
  // then hand control back to the caller's optional onSuccess so it can chain a
  // follow-up step (e.g. open bank linking).
  const callerOnSuccess = openOptions?.onSuccess;
  const handleAuthSuccess = useCallback(
    (result: BloomAuthSuccess) => {
      closeAuthModal();
      callerOnSuccess?.(result);
    },
    [callerOnSuccess, closeAuthModal]
  );

  const contextValue = useMemo<AuthModalContextValue>(
    () => ({ closeAuthModal, isAuthModalOpen: isOpen, openAuthModal }),
    [closeAuthModal, isOpen, openAuthModal]
  );

  return (
    <AuthModalContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <ClientOnly>
          {() => (
            <AuthModalContent
              googleSsoClientId={googleSsoClientId}
              onClose={closeAuthModal}
              onSuccess={handleAuthSuccess}
              skipBagAfterAuth={openOptions?.skipBagAfterAuth}
            />
          )}
        </ClientOnly>
      )}
    </AuthModalContext.Provider>
  );
};

interface AuthModalContentProps {
  googleSsoClientId?: Nullable<string>;
  onClose: () => void;
  onSuccess?: (result: BloomAuthSuccess) => void;
  skipBagAfterAuth?: boolean;
}

const AuthModalContent = ({
  googleSsoClientId,
  onClose,
  onSuccess,
  skipBagAfterAuth,
}: AuthModalContentProps) => {
  const {
    errors,
    onCheckUserExists,
    onForgotPasswordSubmit,
    onSignInSubmit,
    onSignUpSubmit,
  } = useBloomAuthHandlers({ onSuccess, skipBagAfterAuth });

  return (
    <LoginModal
      errors={errors}
      onCheckUserExists={onCheckUserExists}
      onForgotPasswordSubmit={onForgotPasswordSubmit}
      onRequestClose={onClose}
      onSignInSubmit={onSignInSubmit}
      onSignUpSubmit={onSignUpSubmit}
      open
      ssoButtons={
        googleSsoClientId ? (
          <GoogleSignInButton
            googleSsoClientId={googleSsoClientId}
            onSuccess={onSuccess}
            skipBagAfterAuth={skipBagAfterAuth}
          />
        ) : undefined
      }
    />
  );
};
